import { Text, useToast } from '@chakra-ui/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { LoadingSpinner } from 'components/loading/Loading';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select, { StylesConfig } from 'react-select';
import { getMemberByFilters } from 'services/api/requests/capacity/resource/resources';
import { getProductionRoadmapProductsByFilters } from 'services/api/requests/roadmap';

import { filterNotInformedOptions } from '../../utils/functions/filterNotInformedOptions';
import { AUTOCOMPLETE_CONFIG, FILTER_FIELDS, SEARCH_TYPES } from './constants';

interface IAutocompleteInfiniteScrollOptionProps {
	label: string;
	value: string | number;
	data?: any;
}

interface IGenericAutocompleteInfiniteScroll {
	searchType: string;
	inputProps: {
		initialValue?: IAutocompleteInfiniteScrollOptionProps;
		buildOptions: (data: any) => IAutocompleteInfiniteScrollOptionProps;
		joinData: (acc: any[], page: any) => any[];
		extraRequestData?: any;
		[key: string]: any;
	};
}

interface IGenericAutocompleteInfiniteScrollRequestProps {
	searchType: string;
	searchTerm?: string;
	extraRequestData?: any;
}

const customStyles: StylesConfig = {
	control: (provided: Record<string, unknown>, state: any) => ({
		...provided,
		borderRadius: 10,
		border: state.isFocused ? '2px solid #3965ff' : '1px solid #cccccc',
		'&:hover': {
			border: '2px solid #3965ff',
		},
	}),
};

const GetRequestProps = (
	props: IGenericAutocompleteInfiniteScrollRequestProps
): any => {
	const { searchType, searchTerm, extraRequestData = {} } = props;

	function getRequest() {
		const requestTypeMap = {
			[SEARCH_TYPES.ROTEIRO_PRODUCAO]:
				getProductionRoadmapProductsByFilters,
			[SEARCH_TYPES.MEMBRO]: getMemberByFilters,
		};

		const selectedRequest = requestTypeMap[searchType];

		if (!selectedRequest) {
			throw new Error('Pesquisa para autocomplete não implementada!');
		}

		return selectedRequest;
	}

	return useInfiniteQuery({
		queryKey: [
			`${searchType}-autocomplete-infinity-scroll`,
			searchTerm,
			...Object.values(extraRequestData),
		],
		queryFn: ({ pageParam = 0 }) =>
			getRequest()({
				page: pageParam,
				size: AUTOCOMPLETE_CONFIG.MAX_RESULTS_PAGE,
				busca: searchTerm,
				...extraRequestData,
			}),
		getNextPageParam: (lastPage, allPages) =>
			lastPage?.length === AUTOCOMPLETE_CONFIG.MAX_RESULTS_PAGE
				? allPages?.length
				: undefined,
		retry: false,
		refetchOnWindowFocus: false,
	});
};

const GenericAutocompleteInfiniteScrollComponent = ({
	searchType,
	inputProps,
}: IGenericAutocompleteInfiniteScroll) => {
	const {
		initialValue,
		buildOptions,
		extraRequestData,
		joinData = (acc, page) => [...acc, ...(page?.length ? page : [])],
		...othersInputProps
	} = inputProps;

	const toast = useToast();

	const timer = useRef<any>(null);
	const [search, setSearch] = useState<string>('');
	const [showError, setShowError] = useState<boolean>(false);

	const { data, error, isFetching, isLoading, fetchNextPage, hasNextPage } =
		GetRequestProps({
			searchType,
			searchTerm: search,
			extraRequestData,
		});

	const allData = useMemo(() => {
		if (!data?.pages) return [];

		const joinedData = data.pages.reduce(joinData, []);

		let filteredData = joinedData;

		const filterField = FILTER_FIELDS[searchType];
		if (filterField) {
			filteredData = filterNotInformedOptions(joinedData, filterField);
		}

		return filteredData;
	}, [data?.pages, joinData, searchType]);

	const memoizedData = useMemo(
		() => allData?.map?.(buildOptions) || [],
		[allData, buildOptions]
	);

	const onInputChange = useCallback(value => {
		if (timer.current) clearTimeout(timer.current);

		timer.current = setTimeout(() => {
			setSearch(value);
		}, AUTOCOMPLETE_CONFIG.INFINITE_SCROLL_DELAY);
	}, []);

	const onMenuScrollToBottom = useCallback(() => {
		if (hasNextPage) fetchNextPage();
	}, [hasNextPage, fetchNextPage]);

	useEffect(() => {
		if (!showError && error) {
			toast({
				title: 'Erro!',
				description: 'Erro ao buscar dados.',
				status: 'error',
				duration: 9000,
				isClosable: true,
				position: 'bottom-left',
			});
			setShowError(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error, toast]);

	const buildAutocomplete = useCallback(() => {
		return (
			<Select
				styles={customStyles}
				defaultInputValue={initialValue?.label}
				className='option-item_autocomplete_infinite_scroll'
				options={memoizedData}
				isLoading={isLoading || isFetching}
				isClearable
				noOptionsMessage={() => (
					<Text color='easyBLUE.300'>
						Nenhum resultado encontrado
					</Text>
				)}
				loadingMessage={() => <LoadingSpinner />}
				placeholder='Digite para buscar'
				onInputChange={onInputChange}
				onMenuScrollToBottom={onMenuScrollToBottom}
				maxMenuHeight={200}
				{...othersInputProps}
			/>
		);
	}, [
		initialValue,
		memoizedData,
		isLoading,
		isFetching,
		onInputChange,
		onMenuScrollToBottom,
		othersInputProps,
	]);

	return buildAutocomplete();
};

const GenericAutocompleteInfiniteScroll = memo(
	GenericAutocompleteInfiniteScrollComponent
);

export default GenericAutocompleteInfiniteScroll;
